import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Briefcase, Users, Award, FileText, Target, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../utils/mockData';

const Services = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 pt-32 bg-[#0a1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 break-words px-4">Our Services</h1>
            <p className="text-lg sm:text-xl text-gray-300 px-4">
              Comprehensive recruiting solutions designed to connect the right talent with the right opportunities
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {mockData.services.map((service, idx) => (
              <div key={service.id} className={`mb-16 ${idx !== mockData.services.length - 1 ? 'pb-16 border-b border-gray-200' : ''}`}>
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className={idx % 2 === 1 ? 'md:order-2' : ''}>
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-6">
                      {idx === 0 && <Briefcase className="text-[#0a1628]" size={32} />}
                      {idx === 1 && <Award className="text-[#0a1628]" size={32} />}
                      {idx === 2 && <FileText className="text-[#0a1628]" size={32} />}
                    </div>
                    <h2 className="text-3xl font-bold text-[#0a1628] mb-4">{service.title}</h2>
                    <p className="text-lg text-gray-700 mb-6">{service.description}</p>
                    <ul className="space-y-3">
                      {service.items.map((item, itemIdx) => (
                        <li key={`${service.id}-${item}`} className="flex items-start space-x-3">
                          <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={idx % 2 === 1 ? 'md:order-1' : ''}>
                    <img 
                      src={
                        idx === 0 ? "https://images.pexels.com/photos/5439447/pexels-photo-5439447.jpeg" :
                        idx === 1 ? "https://customer-assets.emergentagent.com/job_skilled-talent/artifacts/wx16jzbr_31814.jpg" :
                        "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c"
                      }
                      alt={service.title}
                      className="rounded-lg shadow-xl w-full h-80 object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operations Highlights */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">How We Operate</h2>
              <p className="text-xl text-gray-600">
                Our streamlined approach delivers results faster
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mockData.operations.map((operation, idx) => {
                const IconComponent = {
                  'Target': Target,
                  'Briefcase': Briefcase,
                  'Clock': TrendingUp,
                  'Users': Users,
                  'CheckCircle': CheckCircle,
                  'Handshake': Award
                }[operation.icon] || CheckCircle;
                
                return (
                  <Card key={operation.title} className="border-2 border-gray-200 hover:border-[#d4a574] transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-center w-12 h-12 bg-[#d4a574] rounded-lg mb-4">
                        <IconComponent className="text-[#0a1628]" size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-[#0a1628] mb-2">{operation.title}</h3>
                      <p className="text-gray-600 text-sm">{operation.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Service Approach */}
      <section className="py-20 bg-[#0a1628] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">Targeted Recruiting, Not "Spray and Pray"</h2>
            <p className="text-xl text-gray-300 mb-12">
              We focus on quality over quantity, delivering highly qualified candidates who are the right fit for your organization, not just another resume in the pile.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#d4a574] mb-2">30</div>
                <p className="text-lg text-gray-300">Minutes to assess<br />your needs</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#d4a574] mb-2">100%</div>
                <p className="text-lg text-gray-300">Direct hire<br />placements</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#d4a574] mb-2">24/7</div>
                <p className="text-lg text-gray-300">Commitment to<br />your success</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-12 text-center shadow-xl">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
              Ready to Find Your Next Great Hire?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Let's discuss how we can help you build a high-performing team with mission-driven talent.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=hire">
                <Button size="lg" className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]">
                  Hire Talent <ArrowRight className="ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628] hover:text-white">
                  Schedule a Discovery Call
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
